import { useState, useRef, useEffect } from 'react';
import { BsCalendar2 } from 'react-icons/bs';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

interface Props {
  dateFrom: string;
  dateTo: string;
  onChange: (from: string, to: string) => void;
  className?: string;
}

const toDate = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const toLocalISO = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const fmt = (date: Date) =>
  date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const DateRangePicker: React.FC<Props> = ({ dateFrom, dateTo, onChange, className }) => {
  const [open, setOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => toDate(dateFrom));
  const [tempStart, setTempStart] = useState<Date | null>(null);
  const [pickingEnd, setPickingEnd] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setTempStart(null);
        setPickingEnd(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const startDate = toDate(dateFrom);
  const endDate = toDate(dateTo);

  const days = (() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const arr: (Date | null)[] = Array(firstDay).fill(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
    return arr;
  })();

  const handleDateClick = (date: Date) => {
    if (!pickingEnd) {
      setTempStart(date);
      setPickingEnd(true);
    } else {
      const first = tempStart as Date;
      const [from, to] = first <= date ? [first, date] : [date, first];
      onChange(toLocalISO(from), toLocalISO(to));
      setTempStart(null);
      setPickingEnd(false);
      setOpen(false);
    }
  };

  const changeMonth = (inc: number) => {
    setCurrentMonth(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + inc);
      return d;
    });
  };

  return (
    <div className={`relative ${className ?? ''}`} ref={ref}>
      <button
        onClick={() => setOpen(prev => !prev)}
        className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-red-200 bg-white text-sm text-red-500 font-medium hover:bg-red-50 transition-colors whitespace-nowrap"
      >
        <BsCalendar2 className="text-red-400 text-sm flex-shrink-0" />
        {fmt(startDate)} – {fmt(endDate)}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 bg-white border border-gray-100 rounded-xl shadow-lg p-4 z-50 w-72">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => changeMonth(-1)} className="p-1 rounded-full hover:bg-gray-100">
              <MdChevronLeft size={20} />
            </button>
            <span className="text-sm font-semibold text-gray-700">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button onClick={() => changeMonth(1)} className="p-1 rounded-full hover:bg-gray-100">
              <MdChevronRight size={20} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
              <div key={d} className="text-center text-[10px] text-gray-400 font-medium">
                {d}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((date, i) => {
              if (!date) return <div key={i} />;
              const isPickedStart = tempStart && sameDay(date, tempStart);
              const isStart = !pickingEnd && sameDay(date, startDate);
              const isEnd = !pickingEnd && sameDay(date, endDate);
              const inRange =
                !pickingEnd && date > startDate && date < endDate;
              const highlighted = isStart || isEnd || isPickedStart;
              return (
                <div
                  key={i}
                  onClick={() => handleDateClick(date)}
                  className={`h-8 w-8 flex items-center justify-center text-xs cursor-pointer rounded-full transition-colors
                    ${highlighted ? 'bg-blue-600 text-white hover:bg-blue-700' : 'hover:bg-blue-100 text-gray-700'}
                    ${inRange ? 'bg-blue-100 rounded-none' : ''}
                  `}
                >
                  {date.getDate()}
                </div>
              );
            })}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
            <p className="text-xs text-gray-400">
              {pickingEnd ? 'Now select end date' : 'Click a date to start'}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const now = new Date();
                  const from = toLocalISO(new Date(now.getFullYear(), now.getMonth(), 1));
                  const to   = toLocalISO(now);
                  onChange(from, to);
                  setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
                  setTempStart(null);
                  setPickingEnd(false);
                  setOpen(false);
                }}
                className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"
              >
                Reset
              </button>
              <button
                onClick={() => { setTempStart(null); setPickingEnd(false); setOpen(false); }}
                className="bg-blue-600 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
